import { useState } from 'react';

import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useHasRole } from '@/lib/hooks/useHasRole';
import { type Memo, createMemo, deleteMemo, listMemos, updateMemo } from '@/lib/api/memos';

import { MemoDeleteDialog } from './MemoDeleteDialog';

interface Props {
  residentId: string;
}

const ROLE_LABEL: Record<Memo['author']['role'], string> = {
  ADMIN: '관리자',
  MANAGER: '책임',
  STAFF: '직원',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const msg = (err.response?.data as { message?: string | string[] } | undefined)?.message;
    return Array.isArray(msg) ? msg.join(', ') : (msg ?? '요청 처리 중 오류가 발생했습니다');
  }
  return '알 수 없는 오류가 발생했습니다';
}

/**
 * 의뢰서 5-2 탭1 담당자 메모 (P4-3).
 *
 * - 작성/수정/삭제는 MANAGER 이상만 (서버 가드 + UI 노출 제어)
 * - 본인 작성 메모만 수정/삭제 가능 (서버 검증; ADMIN은 예외)
 * - 조회는 전 직원
 * - 모든 CUD는 감사 로그 자동 기록 (P0-8)
 */
export function MemoSection({ residentId }: Props) {
  const queryClient = useQueryClient();
  const canWrite = useHasRole('MANAGER', 'ADMIN');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; content: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Memo | null>(null);

  const memosQuery = useQuery({
    queryKey: ['memos', residentId],
    queryFn: () => listMemos(residentId),
    staleTime: 30 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['memos', residentId] });

  const createMutation = useMutation({
    mutationFn: (content: string) => createMemo(residentId, content),
    onSuccess: () => {
      setDraft('');
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      updateMemo(residentId, id, content),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMemo(residentId, deleteTarget.id);
    setError(null);
    void invalidate();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          담당자 메모
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {memosQuery.data?.length ?? 0}건
        </Typography>
      </Stack>

      {canWrite ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <TextField
            placeholder="입소자 관련 메모를 입력하세요"
            multiline
            minRows={2}
            maxRows={6}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={createMutation.isPending}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.disabled">
              {draft.length} / 2000
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={draft.trim().length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate(draft.trim())}
            >
              {createMutation.isPending ? '저장 중…' : '저장'}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          메모 작성은 책임 담당자 이상 권한이 필요합니다.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {memosQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={20} />
        </Stack>
      ) : (memosQuery.data ?? []).length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
          아직 작성된 메모가 없습니다.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {(memosQuery.data ?? []).map((m) => {
            const isEditing = editing?.id === m.id;
            return (
              <Box
                key={m.id}
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      {m.author.nickname}
                    </Typography>
                    <Chip
                      size="small"
                      label={ROLE_LABEL[m.author.role]}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" color="text.disabled">
                      {formatTime(m.createdAt)}
                      {m.updatedAt !== m.createdAt && ' · 수정됨'}
                    </Typography>
                  </Stack>
                  {m.editableByMe && !isEditing && (
                    <Stack direction="row">
                      <Tooltip title="수정">
                        <IconButton
                          size="small"
                          onClick={() => setEditing({ id: m.id, content: m.content })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(m)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
                {isEditing ? (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      multiline
                      minRows={2}
                      maxRows={6}
                      value={editing.content}
                      onChange={(e) => setEditing({ id: editing.id, content: e.target.value })}
                      slotProps={{ htmlInput: { maxLength: 2000 } }}
                      disabled={updateMutation.isPending}
                    />
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button
                        size="small"
                        onClick={() => setEditing(null)}
                        disabled={updateMutation.isPending}
                      >
                        취소
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          editing.content.trim().length === 0 ||
                          editing.content.trim() === m.content ||
                          updateMutation.isPending
                        }
                        onClick={() =>
                          updateMutation.mutate({ id: editing.id, content: editing.content.trim() })
                        }
                      >
                        {updateMutation.isPending ? '저장 중…' : '저장'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {m.content}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <MemoDeleteDialog
        open={deleteTarget !== null}
        preview={deleteTarget?.content ?? null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
